// Design tokens and constants for the workshop dashboard (Tilannekuva).
// specs/workshop-dashboard.md, issue #1156 (parent #1152).
//
// The primary surface is the 86" 4K room touch panel read from up to ~8 m:
// the clamp() scales below hit the spec's px floors there (headline numerals
// 160–240 px, labels ≥ 80 px) while staying sane on tablets and phones.
// 1vmin on the 4K panel in landscape = 21.6 px.

// The look is Ongelmakartta v2's (#1497; screens/systemic-v2/lensTheme.js →
// BASE, SummaryMapScreen, NodeCarousel): the dark gradient, near-black glassy
// panels with hairline borders, green for "on" and done, yellow for the one
// thing to look at now, red/green for reject/approve.
export const DASH_COLORS = {
  bg: '#0b0d12',
  bgGradient: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
  tile: 'rgba(18, 21, 28, 0.9)',
  tileBorder: 'rgba(255, 255, 255, 0.09)',
  tilePressed: 'rgba(40, 40, 40, 0.9)',
  tileShadow: '0 8px 30px rgba(0, 0, 0, 0.45)',
  // Neutral fills inside a tile: cards, chat bubbles, bar tracks.
  wash: 'rgba(255, 255, 255, 0.04)',
  track: 'rgba(255, 255, 255, 0.08)',
  text: '#e8ecf2',
  textDim: '#9aa3b2',
  accent: '#7dd3a0',
  accentSoft: 'rgba(125, 211, 160, 0.14)',
  // The live flow step only: v2's highlight yellow.
  live: '#ffd54f',
  warn: '#ff9f5a',
  error: '#ff8a8a',
};

export const DASH_FONTS = {
  // Rational is loaded app-wide (index.html); v2 sets its titles in it.
  numeric: "'Rational', system-ui, sans-serif",
  ui: "system-ui, 'Segoe UI', sans-serif",
};

export const DASH_TYPE = {
  // ~216 px on the 4K panel; floors keep phones readable.
  headline: 'clamp(40px, 10vmin, 260px)',
  // ~82 px on the panel — the spec's "readable from your seat" floor.
  label: 'clamp(15px, 3.8vmin, 96px)',
  // Secondary text; people walk up to read details.
  meta: 'clamp(12px, 1.7vmin, 42px)',
};

// Spec: touch targets ≥ 60 px on the big screen (standing touch at arm's
// length); 44 px stays the absolute floor on small devices.
export const MIN_TOUCH_PX = 60;

// Socket-first with a slow poll as fallback, the InfoWall pattern.
export const POLL_INTERVAL_MS = 60000;
// Socket events coalesce into refetches at most this often.
export const MIN_REFETCH_GAP_MS = 5000;

// Events that mean "numbers behind some tile changed". All already emitted
// to the instance room (apps/docs/docs/arch/socket-events.md), except
// flow-timer-state (#1153) and ai-call-logged (#1155), which arrive with
// their slices — listening early is harmless.
export const REFRESH_EVENTS = [
  'flow-activated',
  'flow-step-changed',
  'flow-paused',
  'flow-resumed',
  'flow-deactivated',
  'flow-timer-state',
  'screen-state-changed',
  'instance-content-changed',
  'scenario-tulevaisuuskuva-swapped',
  'impact-path-node-added',
  'solution-vote-updated',
  'pivot-vote-updated',
  'node-vote-updated',
];

// Emitted per logged AI call (#1155); drives the cost tile's pulse.
export const AI_CALL_EVENT = 'ai-call-logged';

// One workshop event per thing a participant created or AI result that landed
// (#1261, specs/workshop-events.md): `{ id, at, actor, kind, moduleId }`,
// content-free. The feed's only live source; the vocabulary (labels per
// kind) arrives in the dashboard payload as activity.kinds.
export const WORKSHOP_EVENT = 'workshop-event';
